import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const JenniferAuthority = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });

  const imageFilter = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    ['grayscale(100%) brightness(0.4)', 'grayscale(60%) brightness(0.65)', 'grayscale(20%) brightness(0.85)', 'grayscale(0%) brightness(1)']
  );
  const redOverlay = useTransform(scrollYProgress, [0, 1], [0, 0.12]);

  return (
    <section ref={ref} className="relative bg-darker border-t border-white/5 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[100vh]">
        {/* Sticky Parallax Image */}
        <div className="relative lg:sticky lg:top-0 lg:h-screen overflow-hidden order-2 lg:order-1">
          <motion.img
            src="https://media-manager-c.questera.ai/greta-media/8c84f85263efda659889de32f1f32e795be69e9a2504f254db44deecf03d5d2eaf8d8138b6c4ad97d7d004f4d5618742/images/aW1hZ2UvcG5n/0a8dbd1ab6196d088ee699a1d0e6492e.png"
            alt="Jennifer Gräser"
            style={{ filter: imageFilter }}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          {/* Red shadow overlay */}
          <motion.div className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(135deg, rgba(114,47,55,0.3), transparent 60%)', opacity: redOverlay }} />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-darker lg:block hidden" />
          <div className="absolute inset-0 bg-gradient-to-t from-darker via-transparent to-transparent" />

          {/* Floating quote overlay */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.5 }}
            className="absolute bottom-10 left-6 right-6 lg:right-12 p-6 z-10"
            style={{ background: 'rgba(5,5,5,0.7)', backdropFilter: 'blur(10px)', border: '1px solid rgba(114,47,55,0.3)' }}>
            <p className="text-base font-serif italic text-cream/80 leading-relaxed">
              "Nicht aus Büchern. Nicht aus starren Konzepten.
              Sondern aus{' '}
              <span className="text-wine-light">purer Erfahrung</span> und
              erwachtem Bewusstsein."
            </p>
          </motion.div>
        </div>

        {/* Text Content */}
        <div className="relative z-10 flex flex-col justify-center px-8 md:px-14 py-24 space-y-8 order-1 lg:order-2">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            className="text-wine-light tracking-[0.3em] uppercase text-xs font-semibold">
            Jennifers Geschichte
          </motion.p>

          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.9 }}
            className="text-4xl md:text-5xl font-serif text-cream leading-tight">
            Dies ist kein theoretisches Coaching.{' '}
            <span className="italic text-wine-light block mt-2">
              Es ist eine Reise, die ich selbst gegangen bin.
            </span>
          </motion.h2>

          {[
            {
              delay: 0.2,
              text: 'Seit über 6 Jahren befinde ich mich im Prozess der tiefen inneren Heilung – durch Schmerz, Transformation, extremes Loslassen und Wiederaufbau.',
            },
            {
              delay: 0.4,
              highlight: true,
              text: 'Ich kenne die absolute Dunkelheit, die Zweifel, das Gefühl, festzustecken. Aber ich kenne auch genau den Moment, in dem plötzlich das Licht durchbricht.',
            },
            {
              delay: 0.6,
              text: 'Wenn du beginnst, dich wirklich zu spüren, dich tief zu verstehen und dich bedingungslos zu lieben.',
            },
            {
              delay: 0.8,
              gold: true,
              text: 'Genau deshalb kann ich dich auf diesem Weg begleiten. Nicht aus Büchern. Nicht aus starren Konzepten. Sondern aus purer Erfahrung und erwachtem Bewusstsein.',
            },
          ].map((block, i) => (
            <motion.p key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: block.delay, duration: 0.8 }}
              className={`font-light leading-relaxed text-lg ${
                block.gold ? 'text-gold font-serif italic text-xl' :
                block.highlight ? 'text-cream font-serif italic text-xl' :
                'text-cream/60'
              }`}>
              {block.text}
            </motion.p>
          ))}

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 1 }}
            className="pt-4">
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(212,175,55,0.6), 0 0 100px rgba(212,175,55,0.25)' }}
                whileTap={{ scale: 0.97 }}
                className="inline-block px-12 py-5 bg-gold-gradient text-dark font-bold uppercase tracking-widest text-sm cursor-pointer relative overflow-hidden group"
                style={{ boxShadow: '0 0 25px rgba(212,175,55,0.35)', borderRadius: '2px' }}>
                <span className="relative z-10">✦ Kostenfreies Erstgespräch anfragen</span>
                <div className="absolute inset-0 bg-white/10 translate-x-[-110%] skew-x-[-20deg] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
              </motion.div>
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }} transition={{ delay: 1.2 }}
            className="pt-6 border-t border-white/10">
            <p className="text-cream font-semibold tracking-widest uppercase text-sm">Jennifer Gräser</p>
            <p className="text-gold text-xs uppercase tracking-widest mt-1">Gründerin · Coaching Female</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default JenniferAuthority;